import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Archive, ChevronDown, Plus, Upload, UserPlus } from "lucide-react";

import { getCustomers } from "src/api/customer";
import { useAuth } from "src/providers/AuthProvider";
import CustomModal from "src/components/CustomModal";
import NewCustomerModal from "src/components/modals/NewCustomerModal";
import BulkCustomerModal from "src/components/modals/BulkCustomerModal";
import AddSubAccount from "src/components/modals/AddSubAccount";
import CustomerDetailModal from "src/components/customers/CustomerDetailModal";
import ArchivedCustomersModal from "src/components/customers/ArchivedCustomersModal";
import { Button } from "src/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import { DataTable, PageHead, SearchField, type Column, type RowsPerPageOption } from "src/components/common";
import { getCustomizationValue } from "src/lib/utils";
import type { CustomerRow } from "src/types/customer";

const Customers = () => {
	const { user } = useAuth();
	const [searchParams, setSearchParams] = useSearchParams();
	const [addSingleOpen, setAddSingleOpen] = useState(false);
	const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

	const customerLabel = getCustomizationValue(user, "customer_label", true) || "Customer";
	const customerLabelPlural = getCustomizationValue(user, "customer_label") || "Customers";
	const subAccountLabel = getCustomizationValue(user, "sub_account_label", true) || "Sub Account";
	const isSchool = user?.organization_type_name === "Schools";

	const search = searchParams.get("search") ?? undefined;
	const page = Number(searchParams.get("page") ?? 1);
	const limitParam = searchParams.get("limit") ?? "10";
	const limit: RowsPerPageOption = limitParam === "All" ? "All" : Number(limitParam);

	// `?customer=<uuid>` and `?archived=1` drive the modals, so the old routes can redirect
	// here and still deep-link (decision D2).
	const activeCustomer = searchParams.get("customer");
	const archivedOpen = searchParams.get("archived") === "1";

	const setParam = (key: string, value: string | null) => {
		const next = new URLSearchParams(searchParams);
		if (value === null) next.delete(key);
		else next.set(key, value);
		setSearchParams(next);
	};

	const { data, isLoading } = useQuery({
		queryKey: ["customers", page, limit, search],
		queryFn: () => getCustomers({ page, limit: limit === "All" ? 9999 : limit, search }),
	});

	const list = data?.data;
	const customers = list?.data ?? [];
	const total = list?.total ?? customers.length;

	const columns: Column<CustomerRow>[] = [
		{ key: "customer_id", header: "Cust ID", cell: (row) => row.customer_id ?? "—" },
		{
			key: "name",
			header: "Customer",
			primary: true,
			cell: (row) => (
				<span className="tw-font-medium tw-text-text">{[row.first_name, row.last_name].filter(Boolean).join(" ") || "—"}</span>
			),
		},
		{ key: "business", header: "Business", cell: (row) => row.business_name || "—" },
		{ key: "email", header: "Email", cell: (row) => row.email || "—" },
		{ key: "phone", header: "Phone", cell: (row) => row.phone || "—" },
		{
			key: "actions",
			header: "",
			hideOnMobile: true,
			className: "tw-w-[120px] tw-text-right",
			cell: (row) => (
				<div className="tw-flex tw-justify-end tw-gap-1" onClick={(event) => event.stopPropagation()}>
					<button
						type="button"
						onClick={() => setParam("customer", row.uuid)}
						className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-teal hover:tw-bg-teal-tint"
					>
						View
					</button>
					{isSchool && (
						<CustomModal
							title={`Add ${subAccountLabel}`}
							trigger={
								<button
									type="button"
									className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-text-soft hover:tw-bg-bg hover:tw-text-text"
								>
									Add {subAccountLabel}
								</button>
							}
						>
							<AddSubAccount customer_id={row.id} />
						</CustomModal>
					)}
				</div>
			),
		},
	];

	return (
		<div className="tw-space-y-1">
			<PageHead
				title={customerLabelPlural}
				subtitle={isLoading ? "Loading..." : `${total} ${total === 1 ? customerLabel.toLowerCase() : customerLabelPlural.toLowerCase()}`}
				actions={
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
								<Plus className="tw-h-4 tw-w-4" />
								New {customerLabel}
								<ChevronDown className="tw-h-4 tw-w-4 tw-opacity-80" />
							</Button>
						</DropdownMenuTrigger>
						{/* onCloseAutoFocus is suppressed so the dropdown doesn't return focus to its trigger
						    after the dialog below has already focused its first field. */}
						<DropdownMenuContent align="end" className="tw-w-[210px]" onCloseAutoFocus={(event) => event.preventDefault()}>
							<DropdownMenuItem onSelect={() => setTimeout(() => setAddSingleOpen(true), 0)}>
								<UserPlus />
								Add Single
							</DropdownMenuItem>
							<DropdownMenuItem onSelect={() => setTimeout(() => setBulkUploadOpen(true), 0)}>
								<Upload />
								Bulk Upload
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuItem onSelect={() => setParam("archived", "1")}>
								<Archive />
								View Archived
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				}
			/>

			<CustomModal title={`Add Single ${customerLabel}`} open={addSingleOpen} onOpenChange={setAddSingleOpen}>
				<NewCustomerModal />
			</CustomModal>
			<CustomModal title={`Bulk Upload ${customerLabelPlural}`} open={bulkUploadOpen} onOpenChange={setBulkUploadOpen}>
				<BulkCustomerModal />
			</CustomModal>

			<div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-3 tw-py-4">
				<SearchField placeholder={`Search ${customerLabelPlural.toLowerCase()}...`} />
			</div>

			<DataTable
				data={customers}
				columns={columns}
				rowKey={(row) => row.uuid}
				loading={isLoading}
				totalRows={total}
				page={page}
				onPageChange={(next) => setParam("page", String(next))}
				rowsPerPage={limit}
				onRowsPerPageChange={(next) => {
					const params = new URLSearchParams(searchParams);
					params.set("limit", String(next));
					params.set("page", "1");
					setSearchParams(params);
				}}
				onRowClick={(row) => setParam("customer", row.uuid)}
				emptyTitle={search ? `No ${customerLabelPlural.toLowerCase()} match "${search}"` : `No ${customerLabelPlural.toLowerCase()} yet`}
				emptyDescription={
					search
						? "Try a different search term."
						: `Add your first ${customerLabel.toLowerCase()} to start raising invoices and collecting payments.`
				}
				emptyAction={
					!search && (
						<CustomModal
							title={`Add Single ${customerLabel}`}
							trigger={
								<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
									<Plus className="tw-h-4 tw-w-4" />
									Add {customerLabel}
								</Button>
							}
						>
							<NewCustomerModal />
						</CustomModal>
					)
				}
			/>

			<CustomerDetailModal uuid={activeCustomer} onOpenChange={(open) => !open && setParam("customer", null)} />

			<ArchivedCustomersModal
				open={archivedOpen}
				onOpenChange={(open) => !open && setParam("archived", null)}
				onViewCustomer={(uuid) => {
					const next = new URLSearchParams(searchParams);
					next.delete("archived");
					next.set("customer", uuid);
					setSearchParams(next);
				}}
			/>
		</div>
	);
};

export default Customers;
